// Core packages
import { formatDate } from '@angular/common';
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';

// Third party packages
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { TranslateService } from '@ngx-translate/core';
import * as FileSaver from "file-saver";
import { combineLatest, merge, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, finalize, startWith, tap } from 'rxjs/operators';

// Core packages
import { MatDialog, MatOptionSelectionChange } from '@angular/material';
import { AuthService } from 'src/app/modules/auth/auth.service';
import { CompaniesService } from "src/app/modules/companies/companies.service";
import { DocumentArchivesService } from "src/app/modules/properties/document-archives.service";
import { PropertiesService } from 'src/app/modules/properties/properties.service';
import { ListSetting } from 'src/app/shared/interfaces/list-setting.interface';
import { Company } from "src/app/shared/models/company/company.model";
import { PropertyInterface } from 'src/app/shared/models/property/property.interface';
import { IUser } from 'src/app/shared/models/user/user.interface';
import { ConfigService } from "src/app/shared/services/config.service";
import { HelperService } from 'src/app/shared/services/helper.service';
import { ListsService } from 'src/app/shared/services/lists.service';
import { ThemeService } from 'src/app/shared/services/theme.service';
import { HistoricalDocsDataSource } from 'src/app/shared/utils/data-sources/historical-docs-source';

@Component({
  selector: 'app-historical-docs-list',
  templateUrl: './historical-docs-list.page.html',
  styleUrls: ['./historical-docs-list.page.scss']
})
export class HistoricalDocsListComponent implements OnInit, AfterViewInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  loggedUserPermissions: string[];
  loggedUserRole: any;
  currentRole: any;

  // BEGIN - Table historical docs
  displayedColumns: string[] = ['index', 'property', 'type', 'content', 'reference', 'dateDoc', 'loaded', 'actions'];
  dataSource: HistoricalDocsDataSource;
  @ViewChild('sort1', { static: true }) sort: MatSort;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  // @ViewChild('input', { static: true }) input: ElementRef;
  propertyFilter: FormControl = new FormControl('');
  typeFilter: FormControl = new FormControl('');
  contentFilter: FormControl = new FormControl('');
  referenceFilter: FormControl = new FormControl('');
  dateDocFilter: FormControl = new FormControl('');
  loadedFilter: FormControl = new FormControl('');
  codeTypeFilter: FormControl = new FormControl('');
  // END - Table historical docs

  activeRoute: string;
  propertyIsLoading = false;
  availableProperties: PropertyInterface[] = [];
  availableTypes = [];
  
  // Dati aggiuntivi elenco/nuovo elemento
  suppliersName: any[] = [];
  yearsOfStorage: any[] = [];
  conductorName: any[] = [];
  nameInterventionArchiving: any[] = [];

  documentTemplateId = "";
  documentTemplate: any[] = [];

  loggedUserMaster = false;
  loggedUserTemporary = false;
  companyData = {};
  company: Company = new Company();

  /**
   * Class constructor
   */
  constructor(
    public dialog: MatDialog,
    public cancelContractDialog: MatDialog,
    private helperService: HelperService,
    private authService: AuthService,
    private translateService: TranslateService,
    private themeService: ThemeService,
    private listsService: ListsService,
    private propertiesService: PropertiesService,
    private configService: ConfigService,
    private companiesService: CompaniesService,
    private documentArchivesService: DocumentArchivesService,
  ) { }

  /**
   * Init component
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    // Set page title
    this.themeService.setPageTitle(this.translateService.instant('historicalDocs.list.pageTitle'));

    // Get logged user data
    this.subscriptions.push(this.authService.loggedUser$.subscribe(res => this.loggedUser = res));
    this.subscriptions.push(this.authService.loggedUserPermissions$.subscribe(res => this.loggedUserPermissions = res));

    // Get logged user's role data
    this.currentRole = this.loggedUser.userRoles.find((obj: any) => {
      return (
        obj._id.toString() ===
        this.authService.loggedUserRequestedRole$.getValue().toString()
      );
    });
    this.loggedUserRole = this.currentRole.role;
    if (
      this.currentRole.role.type === "master" ||
      this.currentRole.role.type === "collaborator"
    ) {
      this.displayedColumns = ['property', 'codeType', 'type', 'content', 'reference', 'dateDoc', 'loaded', 'actions'];
      this.loggedUserMaster = true;
      if (this.currentRole.company._id !== "") {
        this.subscriptions.push(
          this.companiesService
            .get(this.currentRole.company._id)
            .subscribe((res) => {
              if (res.status) {
                this.companyData = res.data;
                this.company.setData(res.data);
              }
            })
        );
      }
    } else if (this.currentRole.role.type === "temporary") {
      this.loggedUserTemporary = true;
    }

    // Init data source
    this.dataSource = new HistoricalDocsDataSource(this.documentArchivesService, this.helperService);

    // Init areas
    this.availableTypes = this.configService.availableDocumentAreas
      .map((element: any) => {
        return {
          value: element,
          label: this.translateService.instant(`properties.documents.areas.${element}`),
        }
      });

    // Subscribe to listsService and update this list settings
    this.subscribeToListsSettings('historicalDocs');
  }

  /**
   * After view init logic
   *
   * @since 1.0.0
   */
  ngAfterViewInit(): void {
    // Load first set of data
    this.loadPage();

    // Reset pagination after sorting
    this.subscriptions.push(this.sort.sortChange.subscribe(() => this.paginator.pageIndex = 0));

    // Once user change sorting OR change page
    // let's reload the page data
    this.subscriptions.push(
      merge(this.sort.sortChange, this.paginator.page)
        .pipe(
          tap(() => this.loadPage())
        )
        .subscribe()
    );

    // Handle server-side filtering through column-specific search
    // Why do we use "null as string"? --> @see https://github.com/ReactiveX/rxjs/issues/4772
    this.subscriptions.push(
      combineLatest([
        this.propertyFilter.valueChanges.pipe(startWith(null as string)),
        this.typeFilter.valueChanges.pipe(startWith(null as string)),
        this.contentFilter.valueChanges.pipe(startWith(null as string)),
        this.referenceFilter.valueChanges.pipe(startWith(null as string)),
        this.dateDocFilter.valueChanges.pipe(startWith(null as string)),
        this.loadedFilter.valueChanges.pipe(startWith(null as string)),
        this.codeTypeFilter.valueChanges.pipe(startWith(null as string)),
      ])
        .pipe(
          debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
          distinctUntilChanged(), // Eliminate duplicate values
        )
        .subscribe(res => {
          // Handle property filter
          if (res) {
            this.propertyIsLoading = true;
            this.getAvailableProperties({
              general: this.propertyFilter.value
            });
          }

          // Load page only if at least one value is not null
          if (!res.every(value => value === null)) {
            this.paginator.pageIndex = 0;
            this.loadPage();
          }
        }
        )
    );
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all historical docs
    this.subscriptions.forEach(sub => sub.unsubscribe());

    // Push current list settings to the listsService
    this.listsService.setListSetting(
      'historicalDocs',
      this.paginator.pageIndex.valueOf(),
      this.paginator.pageSize.valueOf(),
      {
        general: '', // this.input.nativeElement.value,
        property: this.propertyFilter.value,
        type: this.typeFilter.value,
        content: this.contentFilter.value,
        reference: this.referenceFilter.value,
        dateDoc: this.dateDocFilter.value,
        loaded: this.loadedFilter.value,
        codeType: this.codeTypeFilter.value,
      }
    );

    this.authService.checkLastUserDocs(this.loggedUserPermissions);
  }

  /**
   * Load requested data page
   *
   * @since 1.0.0
   */
  loadPage(): void {
    const start = this.paginator.pageIndex * this.paginator.pageSize;
    const length = this.paginator.pageSize;
    const search = {
      general: '', // this.input.nativeElement.value,
      property: this.propertyFilter.value ? this.propertyFilter.value._id : undefined,
      type: this.typeFilter.value,
      content: this.contentFilter.value,
      reference: this.referenceFilter.value,
      dateDoc: this.dateDocFilter.value,
      loaded: this.loadedFilter.value,
      codeType: this.codeTypeFilter.value,
    };
    this.dataSource.loadDocumentsItems(start, length, this.sort.active, this.sort.direction, search);
  }

  /**
   * Export historical docs in a file with given format
   *
   * @since 1.0.0
   */
  onExport(): void {
    const date = formatDate(new Date(), 'yyyyMMddhhmmss', 'en-EN');
    const fileName = `${this.translateService.instant('historicalDocs.list.exportFileName')} ${date}`;
    this.subscriptions.push(
      this.documentArchivesService.getHistoricalDocsExport().subscribe(
        res => {
          const fileBlob = res;
          const blob = new Blob([fileBlob], {
            type: 'application/vnd.ms-excel'
          });
          FileSaver.saveAs(blob, `${fileName}.xlsx`);
        },
        err => {
          this.helperService.handleError(err);
        }
      )
    );
  }

  /**
   * Reset table's filters
   *
   * @since 1.0.0
   */
  onResetFilters(): void {
    // Reset lists service data
    this.listsService.setListSetting('historicalDocs', 0, 10, {});

    // Reset tables' filters
    // this.input.nativeElement.value = '';
    this.propertyFilter.setValue(null);
    this.typeFilter.setValue(null);
    this.contentFilter.setValue(null);
    this.referenceFilter.setValue(null);
    this.dateDocFilter.setValue(null);
    this.loadedFilter.setValue(null);
    this.codeTypeFilter.setValue(null);

    // Reset tables' sorting versus and column
    this.sort.direction = "desc";
    this.sort.active = "loaded";
    this.sort._stateChanges.next();

    // Reset paginator
    this.paginator.pageIndex = 0;
    this.paginator.pageSize = 10;

    // Reload table's data
    this.loadPage();
  }

  /**
   * Reset given filter value and reload the page
   *
   * @since 1.0.0
   */
  onResetFilter(filter: FormControl): void {
    filter.reset();
    this.loadPage();
  }

  /**
   * Subscribe to lists settings service to allow this list
   * to remember current filters once users navigate to another page.
   * In this way once user will come back to this page he will
   * have all previous settings (such as page number, page size, search, ...)
   *
   * @since 1.0.0
   */
  subscribeToListsSettings(name: string): void {
    this.listsService.enable();
    this.subscriptions.push(
      this.listsService.listsSettings$.subscribe(
        (settings: ListSetting[]) => {
          if (settings.length) {
            settings.forEach((setting: ListSetting) => {
              if (setting.name === name) {
                if (setting.pageIndex) {
                  this.paginator.pageIndex = setting.pageIndex;
                }
                if (setting.pageSize) {
                  this.paginator.pageSize = setting.pageSize;
                }
                if (setting.search && setting.search.general) {
                  // this.input.nativeElement.value = setting.search.general;
                }
                if (setting.search && setting.search.property) {
                  this.propertyFilter.setValue(setting.search.property);
                }
                if (setting.search && setting.search.type) {
                  this.typeFilter.setValue(setting.search.type);
                }
                if (setting.search && setting.search.content) {
                  this.contentFilter.setValue(setting.search.content);
                }
                if (setting.search && setting.search.reference) {
                  this.referenceFilter.setValue(setting.search.reference);
                }
                if (setting.search && setting.search.dateDoc) {
                  this.dateDocFilter.setValue(setting.search.dateDoc);
                }
                if (setting.search && setting.search.loaded) {
                  this.loadedFilter.setValue(setting.search.loaded);
                }
                if (setting.search && setting.search.codeType) {
                  this.codeTypeFilter.setValue(setting.search.codeType);
                }
                
              }
            });
          }
        }
      )
    );
  }

  /**
   * Reset general search input and dispath a 'keyup' event
   * so that the table re-render his rows
   *
   * @since 1.0.0
   */
  resetInput(): void {
    // this.input.nativeElement.value = '';
    const event = new KeyboardEvent('keyup');
    // this.input.nativeElement.dispatchEvent(event);
  }


  /**
   * Get the list of available admins and owners
   * used to allow filtering on owner and admin columns
   *
   * @since 1.0.0
   */
  getAvailableProperties(search?: any): void {
    this.subscriptions.push(
      this.propertiesService
        .getList(0, null, null, null, search)
        .pipe(
          finalize(() => {
            this.propertyIsLoading = false;
          })
        )
        .subscribe(res => {
          if (res.status) {
            this.availableProperties = res.data.items;
          }
        })
    );
  }

  /**
   * Used to handle the display of selected result
   *
   * @since 1.0.0
   *
   * @param arg Data to be displayed
   * @returns result string to display or undefined
   */
  displayFn(arg?: PropertyInterface): string | undefined {
    return arg ? `${arg.name}` : undefined;
  }

  /**
   * Handle click on a selected property in the mat-autocomplete list and fires the loadPage()
   *
   * @since 1.0.0
   */
  onPropertyFilterSelected(event: MatOptionSelectionChange): void {
    const value = event.source.value;
    this.propertyFilter.setValue(value, { emitEvent: false });
    this.loadPage();
  }

}
